import React, { useEffect, useState } from "react";
import { Worker } from "../../types/worker.type";
import { api } from "../../api/api";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  DatePicker,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { renderTime } from "../../utils/renderTime";
import { ISalary, SalaryType } from "../../types/salary.type";
import { Currency } from "../../types/currency.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import dayjs from "dayjs";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function Workers() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [reportForm] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isPerDayReportModalOpen, setIsPerDayReportModalOpen] = useState(false);
  const [salary, setSalary] = useState<ISalary | null>(null);
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [isNormModalOpen, setIsNormModalOpen] = useState<boolean>(false);
  const [norm, setNorm] = useState<number | null>(null);
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingWorker, setEditingWorker] = useState<Worker | null>(null);
  const [editForm] = Form.useForm();
  const [isEditSalaryOpen, setIsSalaryOpen] = useState<boolean>(false);
  const [salaryType, setSalaryType] = useState<SalaryType | null>(null);
  const [workerId, setWorkerId] = useState<string | null>(null);

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/worker", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
        },
      });
      setWorkers(response.data.workers);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching workers", error);
      message.error("Ошибка получения сотрудников.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async (workerId: string) => {
    setSalaryLoading(true);
    try {
      const response = await api.get(`/salary/worker/${workerId}`);

      setSalary(response.data.salary);
    } catch (error) {
      console.error("Error fetching salary", error);
      message.error("Ошибка получение записи.");
    } finally {
      setSalaryLoading(false);
    }
  };

  const handleDeleteWorker = async (workerId: string) => {
    try {
      await api.delete(`/worker/${workerId}`);
      message.success("Сотрудник успешно удален.");
      fetchWorkers();
    } catch (error: any) {
      console.error("Error deleting worker", error);
      message.error(error.response.data.error);
    }
  };

  const handleSubmitForm = async (values: Partial<Worker>) => {
    setConfirmLoading(true);

    try {
      await api.post("/worker/", values);
      message.success("Сотрудник успешно создан.");
      fetchWorkers();
      setIsModalOpen(false);
      setConfirmLoading(false);
      form.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error adding worker", error);
      message.error(error.response.data.error);
    }
  };

  const handleUpdateSalary = async () => {
    if (workerId && salaryType !== null) {
      setConfirmLoading(true);
      try {
        const response = await api.put(`/worker/salary-type/${workerId}/`, {
          type: salaryType,
        });
        message.success(response.data.message);
        fetchWorkers();
        setIsSalaryOpen(false);
        setConfirmLoading(false);
      } catch (error: any) {
        console.error("Error updating salary type", error);
        message.error(error.response.data.error);
        setConfirmLoading(false);
      }
    }
  };


  const handleReportOpen = (workerId: string, type: SalaryType) => {
    if (type === SalaryType.PERDAY) {
      setIsPerDayReportModalOpen(true);
      setWorkerId(workerId);
    } else {
      setIsReportModalOpen(true);
      fetchPricing(workerId);
    }
  };

  const handleReportCreate = async (values: any) => {
    const details = (salary?.pricings ?? []).map((pricing) => {
      return {
        pricingId: pricing._id,
        dayQuantity: values[`${pricing._id}_day_quantity`],
        nightQuantity: values[`${pricing._id}_night_quantity`],
        wasteQuantity: values[`${pricing._id}_waste_quantity`],
      };
    });

    setConfirmLoading(true);

    try {
      await api.post(`/salary/worker/${salary?.workerId}/report`, {
        yuk: values.yuk,
        details,
        startDate: dayjs(values.startDate).format("DD.MM.YYYY HH:mm"),
        endDate: dayjs(values.endDate).format("DD.MM.YYYY HH:mm"),
      });

      message.success("Отчет успешно создан.");
      fetchWorkers();
      setIsReportModalOpen(false);
      setConfirmLoading(false);
      reportForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating report", error);
      message.error(error.response.data.error);
    }
  };

  const createPerDayReport = async (workerId: string) => {
    setConfirmLoading(true);

    try {
      await api.post(`/salary/per-day/report/${workerId}`);
      message.success("Отчет успешно создан.");
      fetchWorkers();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating report", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setIsPerDayReportModalOpen(false);
    }
  };

  const handleEditWorker = async (values: Partial<Worker>) => {
    if (!editingWorker) return;

    setConfirmLoading(true);
    try {
      await api.put(`/worker/${editingWorker._id}`, values);
      message.success("Данные сотрудника успешно обновлены.");
      fetchWorkers();
      setIsEditModalOpen(false);
      setEditingWorker(null);
      editForm.resetFields();
    } catch (error: any) {
      console.error("Error updating worker", error);
      message.error(error.response?.data?.error || "Ошибка обновления данных сотрудника");
    } finally {
      setConfirmLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkers();
  }, [page, pageSize, searchTerm]);

  const handleExport = () => {
    downloadFile("/export/workers", "workers_data.xlsx");
  };

  const columns = [
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Фамилия",
      dataIndex: "surname",
      key: "surname",
    },
    {
      title: "Прозвище",
      dataIndex: "nickname",
      key: "nickname",
    },
    {
      title: "Имя пользователя",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Тип зарплаты",
      dataIndex: "salaryType",
      key: "salaryType",
    },
    {
      title: "Валюта зарплаты",
      dataIndex: "salaryCurrency",
      key: "salaryCurrency",
    },
    {
      title: "Станки",
      key: "machines",
      render: (_: any, worker: Worker) => (
        <ol>
          {worker.machines.map((item) => (
            <li key={item.machineId}>{item.machineName}</li>
          ))}
        </ol>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, worker: Worker) => {
        const menu = (
          <Menu>
            <Menu.Item
              onClick={() => {
                const name = `${worker.name} ${worker.surname}`;
                navigate(`/${worker._id}/${name}/salary/salary-reports`);
              }}
            >
              Смотреть отчеты
            </Menu.Item>

            {(authState.user?.role === UserRole.Admin ||
              authState.user?.role === UserRole.Accountant) && (
                <>
                  <Menu.Item
                    onClick={() =>
                      handleReportOpen(worker._id, worker.salaryType)
                    }
                  >
                    Оставить отчет
                  </Menu.Item>
                </>
              )}

            {(authState.user?.role === UserRole.Admin || authState.user?.role === UserRole.Accountant || authState.user?.role === UserRole.Cashier) && (
              <>
                <Menu.Item
                  onClick={() => {
                    setEditingWorker(worker);
                    editForm.setFieldsValue({
                      name: worker.name,
                      surname: worker.surname,
                      nickname: worker.nickname,
                    });
                    setIsEditModalOpen(true);
                  }}
                >
                  Редактировать
                </Menu.Item>
                <Popconfirm
                  title="Вы уверены, что хотите удалить этого сотрудника?"
                  okText="Да"
                  cancelText="Нет"
                  onConfirm={() => handleDeleteWorker(worker._id)}
                >
                  <Menu.Item>Удалить</Menu.Item>
                </Popconfirm>

                <Menu.Item
                  onClick={() => {
                    setWorkerId(worker._id);
                    setSalaryType(worker.salaryType);
                    setIsSalaryOpen(true);
                  }}
                >
                  Изменить зарплату
                </Menu.Item>
              </>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Сотрудники</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search placeholder="Поиск" onSearch={handleSearch} allowClear />
        {(authState.user?.role === UserRole.Admin ||
          authState.user?.role === UserRole.Accountant) && (
            <Button type="primary" onClick={() => setIsModalOpen(true)}>
              Добавить
            </Button>
          )}
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={workers}
        loading={loading}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        rowKey="_id"
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Добавить сотрудника"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="name"
            label="Введите имя сотрудника"
            rules={[{ required: true, message: "Введите имя сотрудника" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="surname"
            label="Введите фамилию сотрудника"
            rules={[{ required: true, message: "Введите фамилию сотрудника" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="nickname"
            label="Введите прозвище сотрудника (необязательно)"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="salaryType"
            label="Выберите тип зарплаты"
            rules={[{ required: true, message: "Выберите тип зарплаты" }]}
          >
            <Select placeholder="Тип зарплаты">
              {Object.values(SalaryType).map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="salaryCurrency"
            label="Выберите валюту зарплаты"
            rules={[{ required: true, message: "Выберите валюту зарплаты" }]}
          >
            <Select placeholder="Валюта зарплаты">
              {Object.values(Currency).map((currency) => (
                <Option key={currency} value={currency}>
                  {currency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="username"
            label="Введите логин"
            rules={[{ required: true, message: "Введите имя пользователя" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="minuteNorm"
            label="Введите норму работы (в минутах)"
            rules={[
              { required: true, message: "Введите норму работы (в минутах)" },
            ]}
          >
            <InputNumber min={60} max={1440} />
          </Form.Item>
          <Form.Item
            name="password"
            label="Введите пароль"
            rules={[{ required: true, message: "Введите пароль" }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Отчет"
        open={isReportModalOpen}
        onCancel={() => setIsReportModalOpen(false)}
        onOk={() => reportForm.submit()}
        confirmLoading={confirmLoading}
        loading={salaryLoading}
      >
        <Form form={reportForm} layout="vertical" onFinish={handleReportCreate}>
          <Form.Item
            name="startDate"
            label="Дата начала"
            rules={[
              {
                required: true,
                message: "Пожалуйста, выберите дату начала смены.",
              },
            ]}
          >
            <DatePicker
              showTime
              style={{ width: "100%" }}
              format={{ format: "DD.MM.YYYY HH:mm" }}
            />
          </Form.Item>

          <Form.Item
            name="endDate"
            label="Дата окончания"
            rules={[
              {
                required: true,
                message: "Пожалуйста, выберите дату окончания смены.",
              },
            ]}
          >
            <DatePicker
              showTime
              style={{ width: "100%" }}
              format={{ format: "DD.MM.YYYY HH:mm" }}
            />
          </Form.Item>
          <Form.Item name="yuk" label="Количество Юк">
            <InputNumber defaultValue={0} />
          </Form.Item>
          {salary &&
            salary.pricings.map((pricing) => (
              <>
                <div>{pricing.forName}</div>
                <Form.Item
                  name={`${pricing._id}_day_quantity`}
                  label="Количество днём"
                >
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item
                  name={`${pricing._id}_night_quantity`}
                  label="Количество ночью"
                >
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item
                  name={`${pricing._id}_waste_quantity`}
                  label="Количество отхода"
                >
                  <InputNumber min={0} />
                </Form.Item>
              </>
            ))}
        </Form>
      </Modal>

      <Modal
        title="Отчет за день"
        open={isPerDayReportModalOpen}
        onCancel={() => setIsPerDayReportModalOpen(false)}
        onOk={() => createPerDayReport(workerId!)}
        confirmLoading={confirmLoading}
      >
        <Form layout="vertical">
          <Form.Item name="quantity" label="Кол-во">
            <InputNumber defaultValue={1} disabled />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Редактировать сотрудника"
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingWorker(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={editForm} onFinish={handleEditWorker} layout="vertical">
          <Form.Item
            name="name"
            label="Имя сотрудника"
            rules={[{ required: true, message: "Введите имя сотрудника" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="surname"
            label="Фамилия сотрудника"
            rules={[{ required: true, message: "Введите фамилию сотрудника" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="nickname"
            label="Прозвище сотрудника (необязательно)"
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Изменить тип зарплаты"
        open={isEditSalaryOpen}
        onCancel={() => setIsSalaryOpen(false)}
        onOk={handleUpdateSalary}
        confirmLoading={confirmLoading}
      >
        <Select
          placeholder="Тип зарплаты"
          value={salaryType}
          onChange={(type) => setSalaryType(type)}
        >
          {Object.values(SalaryType).map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
}
